import { Row } from 'react-bootstrap';
import {
  Col,
  MapView,
  Leaflet,
  ReactLeaflet,
  Card,
  SectionTitle
} from '@elotech/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import './ObraDetalheImovel.react.css';
import TableHeader from 'portaltransparencia-common/components/TableHeader.react';
import React from 'react';

const normalPinIcon = new Leaflet.Icon.Default();
const focusedPinIcon = new (Leaflet.Icon.Default.extend({
  options: {
    className: 'focused-pin'
  }
}))();

export const ObraDetalheImovel = ({ imoveis }) => {
  const mapRef = useRef();
  const pinRef = useRef({});
  const [focusedPin, setFocusedPin] = useState('');

  const renderImovelDados = (localizacao, inPopup) => (
    <>
      <div className="row">
        <div className="col-md-12">
          {inPopup ? (
            <h1>{localizacao.imovel.descricao}</h1>
          ) : (
            <h4>{localizacao.imovel.descricao}</h4>
          )}
        </div>
      </div>
      <div className="row form-group">
        <div className="col-md-9">
          <div className="form-group">
            <label className="label">Rua</label>
            <p>{localizacao.imovel.logradouroDescricao}</p>
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label className="label">Número</label>
            <p>{localizacao.imovel.numero}</p>
          </div>
        </div>
      </div>
      <div className="row form-group">
        <div className="col-md-6">
          <div className="form-group">
            <label className="label">Bairro</label>
            <p>{localizacao.imovel.descricaoBairro}</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label className="label">Cidade</label>
            <p>{localizacao.imovel.cidadeDescricao}</p>
          </div>
        </div>
      </div>
      <div className="row form-group">
        <div className="col-md-6">
          <div className="form-group">
            <label className="label">Coordenadas GMS</label>
            <p>{`${localizacao.sulGrau}° ${localizacao.sulMinuto}' ${localizacao.sulSegundo}" S`}</p>
            <p>{`${localizacao.oesteGrau}° ${localizacao.oesteMinuto}' ${localizacao.oesteSegundo}" O`}</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label className="label">Latitude/Longitude</label>
            <p>{`${localizacao.latitude}`}</p>
            <p>{`${localizacao.longitude}`}</p>
          </div>
        </div>
      </div>
      {inPopup && (
        <div className="row">
          <div className="col-md-12">
            <button
              type="button"
              className="btn btn-default positive"
              onClick={() => {
                window.open(
                  `https://www.google.com/maps/search/?api=1&query=${localizacao.latitude},${localizacao.longitude}`
                );
              }}
            >
              Abrir no mapa
            </button>
          </div>
        </div>
      )}
    </>
  );

  useEffect(() => {
    if (pinRef.current[focusedPin]) {
      pinRef.current[focusedPin].leafletElement.openPopup();
    }

    return () => {
      if (pinRef.current[focusedPin]) {
        pinRef.current[focusedPin].leafletElement.closePopup();
      }
    };
  }, [focusedPin]);

  const renderCard = localizacao => {
    return (
      <Card
        className="mb-xs"
        clickable
        active={localizacao.idOxy === focusedPin}
        key={localizacao.idOxy}
        onClick={() => {
          mapRef.current.leafletElement.flyTo(localizacao.latLng, 15);
          setFocusedPin(old =>
            old === localizacao.idOxy ? '' : localizacao.idOxy
          );
        }}
      >
        {renderImovelDados(localizacao, false)}
      </Card>
    );
  };

  const renderPin = localizacao => {
    return (
      <ReactLeaflet.Marker
        key={localizacao.idOxy}
        position={localizacao.latLng}
        onClick={() => setFocusedPin(localizacao.idOxy)}
        ref={ref => (pinRef.current[localizacao.idOxy] = ref)}
        icon={localizacao.idOxy === focusedPin ? focusedPinIcon : normalPinIcon}
      >
        <ReactLeaflet.Popup
          onClose={() =>
            setFocusedPin(old => (old === localizacao.idOxy ? '' : old))
          }
        >
          {renderImovelDados(localizacao, true)}
        </ReactLeaflet.Popup>
      </ReactLeaflet.Marker>
    );
  };

  const localizacoes = useMemo(
    () =>
      imoveis
        .toJS()
        .flatMap(imovel =>
          imovel.coordenadas.map(coord => ({
            ...coord,
            imovel,
            latLng: new Leaflet.LatLng(coord.latitude, coord.longitude)
          }))
        )
        .filter(coord => coord.latitude && coord.longitude),
    [imoveis]
  );

  const bounds = useMemo(() => {
    if (!localizacoes.length) {
      return undefined;
    }
    const coords = localizacoes.map(coord => coord.latLng);
    const bounds = new Leaflet.LatLngBounds(coords);
    return bounds.pad(0.1);
  }, [localizacoes]);

  if (!localizacoes.length) {
    return (
      <div className="panel-table-outer">
        <table className="panel-table striped fancy">
          <TableHeader empty={true} />
        </table>
      </div>
    );
  }

  return (
    <Row>
      <Col md={8} className="pr-none">
        <MapView ref={mapRef} height="500px" width="100%" bounds={bounds}>
          {localizacoes.map(renderPin)}
        </MapView>
      </Col>
      <Col
        md={4}
        style={{
          maxHeight: '500px',
          overflowY: 'auto'
        }}
      >
        <div className="mr-xs">
          <SectionTitle hasButton marginTop="25px">
            Imóveis em obras
            <button
              type="button"
              className="btn btn-default positive"
              onClick={() => {
                if (bounds) {
                  mapRef.current.leafletElement.flyToBounds(bounds);
                }
              }}
            >
              Recentralizar
            </button>
          </SectionTitle>
          {localizacoes.map(renderCard)}
        </div>
      </Col>
    </Row>
  );
};

export default ObraDetalheImovel;
